import React, { useState, useEffect } from 'react';
import { 
  Box, 
  Typography, 
  Grid, 
  Paper, 
  FormControl, 
  InputLabel, 
  Select, 
  MenuItem, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow,
  Chip,
  Avatar,
  LinearProgress,
  Card,
  CardContent,
  IconButton,
  Tooltip
} from '@mui/material';
import { 
  Public as GlobalIcon,
  Mood as PositiveIcon,
  MoodBad as NegativeIcon,
  SentimentNeutral as NeutralIcon,
  TrendingUp as UpTrendIcon,
  TrendingDown as DownTrendIcon,
  TrendingFlat as FlatTrendIcon,
  Info as InfoIcon
} from '@mui/icons-material';
import { LineChart, Line, XAxis, YAxis, Tooltip as ChartTooltip, ResponsiveContainer } from 'recharts';

// Mock data generators
const generateMockRegions = () => [
  { 
    id: 'north-america', 
    name: 'North America',
    flag: '🇺🇸',
    sentiment: {
      overall: 'Positive',
      score: 0.75,
      trend: 'up'
    },
    sources: [
      { name: 'Salesforce', totalFeedback: 450 },
      { name: 'ServiceNow', totalFeedback: 250 },
      { name: 'Contact Center', totalFeedback: 200 }
    ],
    topFeedback: [
      { 
        id: 'feedback-1', 
        text: 'Exceptional customer support and quick resolution times', 
        source: 'Salesforce',
        sentiment: 'Positive',
        date: '2024-05-15'
      },
      { 
        id: 'feedback-2', 
        text: 'Occasional delays in product updates', 
        source: 'ServiceNow',
        sentiment: 'Neutral',
        date: '2024-05-10'
      },
      { 
        id: 'feedback-3', 
        text: 'Need more comprehensive onboarding resources', 
        source: 'Contact Center',
        sentiment: 'Negative',
        date: '2024-05-05'
      }
    ],
    sentimentTrend: [
      { month: 'Jan', sentiment: 0.6 },
      { month: 'Feb', sentiment: 0.7 },
      { month: 'Mar', sentiment: 0.75 },
      { month: 'Apr', sentiment: 0.8 },
      { month: 'May', sentiment: 0.85 }
    ]
  },
  { 
    id: 'europe', 
    name: 'Europe',
    flag: '🇪🇺',
    sentiment: {
      overall: 'Neutral',
      score: 0.5,
      trend: 'flat'
    },
    sources: [
      { name: 'Salesforce', totalFeedback: 300 },
      { name: 'ServiceNow', totalFeedback: 180 },
      { name: 'Contact Center', totalFeedback: 150 }
    ],
    topFeedback: [
      { 
        id: 'feedback-4', 
        text: 'Impressive data privacy features', 
        source: 'Salesforce',
        sentiment: 'Positive',
        date: '2024-05-12'
      },
      { 
        id: 'feedback-5', 
        text: 'Pricing could be more competitive', 
        source: 'ServiceNow',
        sentiment: 'Negative',
        date: '2024-05-08'
      },
      { 
        id: 'feedback-6', 
        text: 'Stable performance with minor UI improvements needed', 
        source: 'Contact Center',
        sentiment: 'Neutral',
        date: '2024-05-03'
      }
    ],
    sentimentTrend: [
      { month: 'Jan', sentiment: 0.5 },
      { month: 'Feb', sentiment: 0.52 },
      { month: 'Mar', sentiment: 0.5 },
      { month: 'Apr', sentiment: 0.48 },
      { month: 'May', sentiment: 0.5 }
    ]
  },
  { 
    id: 'asia-pacific', 
    name: 'Asia Pacific',
    flag: '🌏',
    sentiment: {
      overall: 'Positive',
      score: 0.7,
      trend: 'up'
    },
    sources: [
      { name: 'Salesforce', totalFeedback: 400 },
      { name: 'ServiceNow', totalFeedback: 220 },
      { name: 'Contact Center', totalFeedback: 180 }
    ],
    topFeedback: [
      { 
        id: 'feedback-7', 
        text: 'Innovative features that solve real business challenges', 
        source: 'Salesforce',
        sentiment: 'Positive',
        date: '2024-05-14'
      },
      { 
        id: 'feedback-8', 
        text: 'Some localization improvements needed', 
        source: 'ServiceNow',
        sentiment: 'Neutral',
        date: '2024-05-09'
      },
      { 
        id: 'feedback-9', 
        text: 'Integration challenges with local systems', 
        source: 'Contact Center',
        sentiment: 'Negative',
        date: '2024-05-04'
      }
    ],
    sentimentTrend: [
      { month: 'Jan', sentiment: 0.6 },
      { month: 'Feb', sentiment: 0.65 },
      { month: 'Mar', sentiment: 0.7 },
      { month: 'Apr', sentiment: 0.75 },
      { month: 'May', sentiment: 0.7 }
    ]
  }
];

const GlobalVOC = ({collapsed}) => {
  const [regions] = useState(generateMockRegions());
  const [selectedRegion, setSelectedRegion] = useState(regions[0]);

  // Sentiment color and icon mapping
  const getSentimentDetails = (sentiment) => {
    switch(sentiment) {
      case 'Positive': 
        return { 
          color: '#10B981', 
          icon: <PositiveIcon sx={{ color: '#10B981' }} /> 
        };
      case 'Negative': 
        return { 
          color: '#EF4444', 
          icon: <NegativeIcon sx={{ color: '#EF4444' }} /> 
        };
      default: 
        return { 
          color: '#F59E0B', 
          icon: <NeutralIcon sx={{ color: '#F59E0B' }} /> 
        };
    }
  };

  // Trend icon mapping
  const getTrendIcon = (trend) => {
    switch(trend) {
      case 'up': 
        return <UpTrendIcon sx={{ color: '#10B981' }} />;
      case 'down': 
        return <DownTrendIcon sx={{ color: '#EF4444' }} />;
      default: 
        return <FlatTrendIcon sx={{ color: '#F59E0B' }} />;
    }
  };

  return (
    <Box 
    sx={{ 
        bgcolor: '#050a18', 
        // minHeight: '100vh', 
        p: { xs: 2, sm: 3, md: 4 }, 
        color: 'white',
        marginLeft: collapsed ? "60px" : "240px", // Add dynamic margin based on sidebar state
        transition: 'margin-left 0.3s ease', // Add smooth transition for sidebar toggle
        '@media (max-width: 1200px)': {
          marginLeft: collapsed ? "60px" : "240px",
        },
        '@media (max-width: 900px)': {
          marginLeft: collapsed ? "60px" : "180px", // Smaller sidebar on medium screens
        },
        '@media (max-width: 600px)': {
          marginLeft: collapsed ? "60px" : "60px", // On mobile, sidebar is likely collapsed
          p: { xs: 1, sm: 2 }, // Smaller padding on mobile
        }
      }}
    >
      <Typography 
        variant="h4" 
        sx={{ 
          mb: 4, 
          fontWeight: 'bold', 
          textAlign: 'center',
          color: '#3B82F6'
        }}
      >
        Global Voice of Customer
      </Typography>

      {/* Region Selector */}
      <Box sx={{ 
        display: 'flex', 
        justifyContent: 'center', 
        mb: 4 
      }}>
        <FormControl variant="outlined" sx={{ 
          minWidth: 300,
          '& .MuiOutlinedInput-root': {
            '& fieldset': {
              borderColor: 'rgba(59, 130, 246, 0.5)',
            },
            '&:hover fieldset': {
              borderColor: '#3B82F6',
            },
            '&.Mui-focused fieldset': {
              borderColor: '#3B82F6',
            },
          }
        }}>
          <InputLabel sx={{ color: 'white', '&.Mui-focused': { color: '#3B82F6' } }}>
            Select Region
          </InputLabel>
          <Select
            value={selectedRegion.id}
            onChange={(e) => setSelectedRegion(
              regions.find(r => r.id === e.target.value)
            )}
            label="Select Region"
            sx={{ 
              color: 'white',
              '& .MuiSelect-icon': { color: 'white' }
            }}
            renderValue={(selectedId) => {
              const region = regions.find(r => r.id === selectedId);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.5rem', mr: 1 }}>
                    {region.flag}
                  </Typography>
                  <Typography color="white">
                    {region.name}
                  </Typography>
                </Box>
              );
            }}
          >
            {regions.map((region) => (
              <MenuItem key={region.id} value={region.id}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <Typography sx={{ fontSize: '1.5rem', mr: 1 }}>
                    {region.flag}
                  </Typography>
                  <Typography color="white">
                    {region.name}
                  </Typography>
                </Box>
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Box>

      <Grid container spacing={3}>
        {/* Sentiment Overview */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: 'rgba(16, 24, 40, 0.8)', 
              p: 3,
              borderRadius: 2,
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              justifyContent: 'space-between',
              mb: 2 
            }}>
              <Typography variant="h6" color="#3B82F6">
                Customer Sentiment
              </Typography>
              {getTrendIcon(selectedRegion.sentiment.trend)}
            </Box>

            <Box sx={{ 
              display: 'flex', 
              alignItems: 'center', 
              mb: 2 
            }}>
              {getSentimentDetails(selectedRegion.sentiment.overall).icon}
              <Typography 
                variant="h4" 
                sx={{ 
                  ml: 2, 
                  color: getSentimentDetails(selectedRegion.sentiment.overall).color 
                }}
              >
                {selectedRegion.sentiment.overall}
              </Typography>
            </Box>

            <LinearProgress 
              variant="determinate" 
              value={selectedRegion.sentiment.score * 100}
              sx={{
                height: 10,
                borderRadius: 5,
                bgcolor: 'rgba(255,255,255,0.1)',
                '& .MuiLinearProgress-bar': {
                  bgcolor: getSentimentDetails(selectedRegion.sentiment.overall).color
                }
              }}
            />
            <Typography 
              variant="body2" 
              sx={{ 
                mt: 1, 
                color: 'rgba(255,255,255,0.7)',
                textAlign: 'center'
              }}
            >
              Sentiment Score: {(selectedRegion.sentiment.score * 100).toFixed(0)}%
            </Typography>
          </Paper>
        </Grid>

        {/* Sentiment Trend Chart */}
        <Grid item xs={12} md={8}>
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: 'rgba(16, 24, 40, 0.8)', 
              p: 3,
              borderRadius: 2,
              border: '1px solid rgba(59, 130, 246, 0.2)',
              height: '100%'
            }}
          >
            <Typography variant="h6" color="#3B82F6" sx={{ mb: 2 }}>
              Sentiment Trend
            </Typography>
            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={selectedRegion.sentimentTrend}>
                <XAxis 
                  dataKey="month" 
                  tick={{ fill: 'white' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                />
                <YAxis 
                  tick={{ fill: 'white' }}
                  axisLine={{ stroke: 'rgba(255,255,255,0.2)' }}
                  domain={[0.4, 0.9]}
                />
                <ChartTooltip 
                  contentStyle={{ 
                    backgroundColor: 'rgba(16, 24, 40, 0.9)', 
                    border: '1px solid rgba(59, 130, 246, 0.2)',
                    color: 'white'
                  }}
                  labelStyle={{ color: '#3B82F6' }}
                />
                <Line 
                  type="monotone" 
                  dataKey="sentiment" 
                  stroke="#3B82F6" 
                  strokeWidth={3}
                  dot={{ 
                    stroke: '#3B82F6', 
                    strokeWidth: 2, 
                    fill: '#3B82F6' 
                  }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Paper>
        </Grid>

        {/* Feedback Sources */}
        <Grid item xs={12} md={4}>
          <Paper 
            elevation={0} 
            sx={{ 
              bgcolor: 'rgba(16, 24, 40, 0.8)', 
              p: 3,
              borderRadius: 2,
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <Typography variant="h6" color="#3B82F6" sx={{ mb: 2 }}>
              Feedback Sources
            </Typography>
            {selectedRegion.sources.map((source) => (
              <Box 
                key={source.name}
                sx={{ 
                  display: 'flex', 
                  justifyContent: 'space-between', 
                  alignItems: 'center',
                  mb: 1,
                  p: 1,
                  bgcolor: 'rgba(13, 26, 48, 0.5)',
                  borderRadius: 1}}
                  >
                    <Typography color="white">{source.name}</Typography>
                    <Chip 
                      label={source.totalFeedback} 
                      size="small"
                      sx={{ 
                        bgcolor: 'rgba(59, 130, 246, 0.2)', 
                        color: '#3B82F6' 
                      }}
                    />
                  </Box>
                ))}
              </Paper>
            </Grid>
    
            {/* Top Feedback */}
            <Grid item xs={12} md={8}>
              <Paper 
                elevation={0} 
                sx={{ 
                  bgcolor: 'rgba(16, 24, 40, 0.8)', 
                  p: 3,
                  borderRadius: 2,
                  border: '1px solid rgba(59, 130, 246, 0.2)'
                }}
              >
                <Typography variant="h6" color="#3B82F6" sx={{ mb: 2 }}>
                  Top Customer Feedback
                </Typography>
                <TableContainer>
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Feedback</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Source</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Sentiment</TableCell>
                        <TableCell sx={{ color: 'white', fontWeight: 'bold' }}>Date</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {selectedRegion.topFeedback.map((feedback) => (
                        <TableRow key={feedback.id} hover>
                          <TableCell sx={{ color: 'white' }}>
                            <Tooltip title={feedback.text} placement="top-start">
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  maxWidth: 300, 
                                  overflow: 'hidden', 
                                  textOverflow: 'ellipsis',
                                  whiteSpace: 'nowrap'
                                }}
                              >
                                {feedback.text}
                              </Typography>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ color: 'white' }}>
                            <Chip 
                              label={feedback.source}
                              size="small"
                              sx={{ 
                                bgcolor: 'rgba(59, 130, 246, 0.2)', 
                                color: '#3B82F6' 
                              }}
                            />
                          </TableCell>
                          <TableCell>
                            <Chip 
                              label={feedback.sentiment}
                              size="small"
                              sx={{ 
                                bgcolor: getSentimentDetails(feedback.sentiment).color + '20', 
                                color: getSentimentDetails(feedback.sentiment).color,
                                fontWeight: 'bold'
                              }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: 'white' }}>
                            {feedback.date}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Paper>
            </Grid>
          </Grid>
    
          {/* Additional Insights Card */}
          <Card 
            sx={{ 
              mt: 3, 
              bgcolor: 'rgba(16, 24, 40, 0.8)', 
              border: '1px solid rgba(59, 130, 246, 0.2)'
            }}
          >
            <CardContent>
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                mb: 2 
              }}>
                <InfoIcon sx={{ color: '#3B82F6', mr: 2 }} />
                <Typography variant="h6" color="#3B82F6">
                  Additional Insights
                </Typography>
              </Box>
              <Grid container spacing={2}>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(13, 26, 48, 0.5)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
                      Total Feedback Volume
                    </Typography>
                    <Typography variant="h5" color="#3B82F6">
                      {selectedRegion.sources.reduce((sum, source) => sum + source.totalFeedback, 0)}
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(13, 26, 48, 0.5)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
                      Positive Feedback Ratio
                    </Typography>
                    <Typography variant="h5" color="#10B981">
                      {(selectedRegion.topFeedback.filter(f => f.sentiment === 'Positive').length / 
                        selectedRegion.topFeedback.length * 100).toFixed(0)}%
                    </Typography>
                  </Paper>
                </Grid>
                <Grid item xs={12} md={4}>
                  <Paper 
                    elevation={0}
                    sx={{ 
                      p: 2, 
                      bgcolor: 'rgba(13, 26, 48, 0.5)',
                      borderRadius: 2
                    }}
                  >
                    <Typography variant="subtitle2" color="rgba(255,255,255,0.7)">
                      Key Improvement Areas
                    </Typography>
                    <Box sx={{ display: 'flex', gap: 1, mt: 1 }}>
                      {['Onboarding', 'UI/UX', 'Performance'].map((area) => (
                        <Chip 
                          key={area}
                          label={area}
                          size="small"
                          sx={{ 
                            bgcolor: 'rgba(59, 130, 246, 0.2)', 
                            color: '#3B82F6' 
                          }}
                        />
                      ))}
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
            </CardContent>
          </Card>
        </Box>
      );
    };
    
    export default GlobalVOC;